import { useState } from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import {
  CalendarOff, HeartPulse, HardHat, Siren, Gift, FileSpreadsheet,
  Landmark, Target, TriangleAlert, CircleHelp, FileCode2, Play, X, ShieldCheck,
} from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { data as fData, moeda, plural } from "../lib/formato.js";
import { useIr } from "../shell/navegar.js";

/**
 * Lista operacional.
 *
 * Nove telas com o mesmo formato (quem, o quê, quando, quanto falta) não pedem
 * nove implementações. O que varia é a consulta e o mapeamento de cada linha,
 * e é só isso que cada tela declara.
 */
type Item = {
  chave: string;
  titulo: string;
  detalhe?: string | undefined;
  marca?: string | undefined;
  /** Negativo é atraso; zero é hoje. Vira o carimbo da direita. */
  dias?: number | null | undefined;
  tom?: "normal" | "atencao" | "critico" | undefined;
  aoAbrir?: (() => void) | undefined;
};

function Lista<T>(
  { q, mapear, vazio, icone: Icone }:
  { q: UseQueryResult<T[]>; mapear: (x: T) => Item;
    vazio: { titulo: string; descricao: string };
    icone: typeof CalendarOff },
) {
  if (q.isPending) return <Carregando linhas={6} />;
  if (q.isError) return <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />;
  if (q.data.length === 0) return <EmptyState titulo={vazio.titulo} descricao={vazio.descricao} />;

  const itens = q.data.map(mapear);
  return (
    <Cartao titulo="Registros" subtitulo={plural(itens.length, "linha", "linhas")}>
      <ul className="eop-op">
        {itens.map((i) => (
          <li key={i.chave} data-tom={i.tom !== "normal" ? i.tom : undefined}>
            <span aria-hidden className="eop-op-icone">
              {i.tom === "critico" ? <TriangleAlert size={15} /> : <Icone size={15} />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="eop-op-titulo">{i.titulo}</span>
              {i.detalhe && <span className="eop-op-sub">{i.detalhe}</span>}
            </span>
            {i.marca && <span className="pilula">{i.marca}</span>}
            {i.dias !== null && i.dias !== undefined && (
              <span className="num eop-op-dias" data-tom={i.tom}>
                {/* Negativo é atraso, não "menos dias". Em DP, atraso é multa. */}
                {i.dias < 0 ? `${Math.abs(i.dias)}d de atraso` : i.dias === 0 ? "hoje" : `${i.dias}d`}
              </span>
            )}
          </li>
        ))}
      </ul>
    </Cartao>
  );
}

/* ----------------------------------------------------------- afastamentos */

export function Afastamentos() {
  const q = useQuery({
    queryKey: ["operacao", "afastamentos"],
    queryFn: () => trpc.operacao.afastamentos.query({ soEmCurso: false }),
  });
  return (
    <Pagina secao="Pessoas" titulo="Afastamentos"
            descricao="Do 1º ao 15º dia quem paga é a empresa; do 16º em diante é o INSS. Recaída em até 60 dias volta a contar do afastamento anterior.">
      <Lista q={q} icone={CalendarOff}
             vazio={{ titulo: "Ninguém afastado",
                      descricao: "Afastamento a partir de 3 dias gera S-2230, e acima de 15 dias muda quem paga." }}
             mapear={(a) => ({
               chave: a.id, titulo: a.nome,
               detalhe: `${a.motivo} · desde ${fData(a.dataInicio)}`
                 + (a.dataFim ? ` até ${fData(a.dataFim)}` : ", sem previsão de retorno")
                 + (a.retornoComRestricao ? " · retorno com restrição" : ""),
               // O 16º dia é o divisor: é quando a conta passa para o INSS.
               marca: a.emCurso ? (a.diasCorridos > 15 ? "INSS" : "empresa") : undefined,
               dias: a.emCurso ? a.diasCorridos : null,
               tom: a.emCurso && a.diasCorridos > 15 ? "atencao" : "normal",
             })} />
    </Pagina>
  );
}

/* -------------------------------------------------------------------- SST */

export function Exames() {
  const q = useQuery({
    queryKey: ["operacao", "asos"],
    queryFn: () => trpc.operacao.asos.query(),
  });
  return (
    <Pagina secao="Saúde e segurança" titulo="Exames ocupacionais"
            descricao="A periodicidade vem da exposição a risco do PGR, não da idade: a regra por faixa etária saiu da NR-07 na revisão de 2022.">
      <Lista q={q} icone={HeartPulse}
             vazio={{ titulo: "Nenhum ASO registrado",
                      descricao: "Todo ASO gera um S-2220, e o admissional precisa ser anterior ao início das atividades." }}
             mapear={(a) => ({
               chave: a.id, titulo: a.nome,
               detalhe: `${a.tipo} em ${fData(a.data)} · ${a.resultado}`
                 + (a.medico ? ` · ${a.medico}${a.crm ? ` CRM ${a.crm}` : ""}` : ""),
               marca: a.resultado === "inapto" ? "inapto" : undefined,
               dias: a.diasAteVencer,
               tom: a.diasAteVencer === null ? "normal"
                 : a.diasAteVencer < 0 ? "critico"
                 : a.diasAteVencer <= 30 ? "atencao" : "normal",
             })} />
    </Pagina>
  );
}

export function Epi() {
  const q = useQuery({
    queryKey: ["operacao", "epi"],
    queryFn: () => trpc.operacao.entregasEpi.query(),
  });
  return (
    <Pagina secao="Saúde e segurança" titulo="EPI"
            descricao="O número e a validade do CA ficam congelados no ato da entrega: o CA do item muda quando o fabricante renova, e a ficha precisa provar qual valia no dia."
    >
      <Lista q={q} icone={HardHat}
             vazio={{ titulo: "Nenhuma entrega registrada",
                      descricao: "A ficha de EPI é o que sustenta a defesa em reclamação por insalubridade." }}
             mapear={(e) => ({
               chave: e.id, titulo: `${e.nome} · ${e.item}`,
               detalhe: `${e.quantidade}x em ${fData(e.data)}`
                 + (e.ca ? ` · CA ${e.ca}` : " · SEM CA")
                 + (e.caValidade ? `, válido até ${fData(e.caValidade)}` : "")
                 + (e.motivo ? ` · ${e.motivo}` : ""),
               // Entregar com CA vencido invalida a ficha inteira daquele item.
               marca: e.caVencidoNoAto ? "CA vencido na entrega" : !e.ca ? "sem CA" : undefined,
               tom: e.caVencidoNoAto || !e.ca ? "critico" : "normal",
             })} />
    </Pagina>
  );
}

export function Acidentes() {
  const q = useQuery({
    queryKey: ["operacao", "cats"],
    queryFn: () => trpc.operacao.cats.query(),
  });
  return (
    <Pagina secao="Saúde e segurança" titulo="Acidentes e CAT"
            descricao="A comunicação vai até o primeiro dia útil seguinte, e é imediata em caso de óbito.">
      <Lista q={q} icone={Siren}
             vazio={{ titulo: "Nenhum acidente registrado",
                      descricao: "A CAT é obrigatória mesmo sem afastamento, e a falta de comunicação é infração autônoma." }}
             mapear={(c) => ({
               chave: c.id, titulo: c.nome,
               detalhe: `${c.tipo} em ${fData(c.dataAcidente)}`
                 + (c.horaAcidente ? ` às ${c.horaAcidente.slice(0, 5)}` : "")
                 + (c.parteAtingida ? ` · ${c.parteAtingida}` : "")
                 + (c.agenteCausador ? ` · ${c.agenteCausador}` : ""),
               marca: c.houveObito ? "óbito"
                 : c.numeroRecibo ? "comunicada" : "a comunicar",
               dias: c.numeroRecibo ? null : c.diasAteOPrazo,
               tom: c.houveObito || (!c.numeroRecibo && (c.diasAteOPrazo ?? 1) <= 0)
                 ? "critico" : c.numeroRecibo ? "normal" : "atencao",
             })} />
    </Pagina>
  );
}

/* ------------------------------------------------------------- benefícios */

export function Beneficios() {
  const q = useQuery({
    queryKey: ["operacao", "adesoes"],
    queryFn: () => trpc.operacao.adesoes.query(),
  });
  return (
    <Pagina secao="Benefícios" titulo="Elegibilidade e adesões"
            descricao="O vale-transporte tem teto de 6% do salário base, e o PAT limita a participação do trabalhador a 20%.">
      <Lista q={q} icone={Gift}
             vazio={{ titulo: "Nenhuma adesão",
                      descricao: "A elegibilidade é avaliada por cargo, tempo de casa, filial e tipo de contrato, e cada decisão fica explicada." }}
             mapear={(a) => ({
               chave: a.id, titulo: `${a.nome} · ${a.beneficio}`,
               detalhe: `${a.tipo}${a.operadora ? ` · ${a.operadora}` : ""}`
                 + ` · desde ${fData(a.inicio)}`
                 + (a.fim ? ` até ${fData(a.fim)}` : "")
                 + (a.percentualEmpregado ? ` · ${a.percentualEmpregado}% do trabalhador` : ""),
               marca: a.inscritoNoPat ? "PAT" : undefined,
               tom: "normal",
             })} />
    </Pagina>
  );
}

export function Faturas() {
  const q = useQuery({
    queryKey: ["operacao", "divergencias"],
    queryFn: () => trpc.operacao.divergencias.query(),
  });
  return (
    <Pagina secao="Benefícios" titulo="Faturas e conciliação"
            descricao="Vida cobrada depois da saída é o que a conciliação encontra, e é dinheiro que volta.">
      <Lista q={q} icone={FileSpreadsheet}
             vazio={{ titulo: "Nenhuma divergência",
                      descricao: "A conciliação compara a fatura da operadora com as vidas que o sistema conhece." }}
             mapear={(d) => ({
               chave: d.id,
               titulo: d.nome ?? d.documento ?? "sem identificação",
               detalhe: `${d.tipo}${d.detalhe ? ` · ${d.detalhe}` : ""}`
                 + (d.valor ? ` · ${moeda(d.valor)}` : ""),
               marca: d.resolvidaEm ? "resolvida" : undefined,
               tom: d.resolvidaEm ? "normal" : "atencao",
             })} />
    </Pagina>
  );
}

/* ---------------------------------------------------------------- eSocial */

export function ESocialPainel() {
  const [vendoXml, setVendoXml] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const qc = useQueryClient();
  const ir = useIr();

  const pendentes = useQuery({
    queryKey: ["esocial", "pendentes"],
    queryFn: () => trpc.transmissao.pendentes.query(),
  });
  const fila = useQuery({
    queryKey: ["esocial", "fila"],
    queryFn: () => trpc.transmissao.fila.query(),
  });
  const certificados = useQuery({
    queryKey: ["esocial", "certificados"],
    queryFn: () => trpc.transmissao.certificados.query(),
  });
  const rejeicoes = useQuery({
    queryKey: ["operacao", "esocial", "ocorrencias"],
    queryFn: () => trpc.operacao.ocorrenciasESocial.query(),
  });

  const gerar = useMutation({
    mutationFn: () => trpc.transmissao.gerar.mutate({
      tipos: ["S-1000", "S-1005", "S-2200", "S-2230"],
    }),
    onSuccess: (r) => {
      // Evento recusado na montagem NÃO é erro do sistema: é dado faltando, e
      // o leiaute recusa aqui o que o governo rejeitaria lá.
      setAviso(
        `${r.gerados.length} eventos montados.`
        + (r.recusados.length
          ? ` ${r.recusados.length} recusados por dado faltando: `
            + r.recusados.slice(0, 3).map((x) => `${x.para} (${x.motivo})`).join("; ")
          : ""));
      void qc.invalidateQueries({ queryKey: ["esocial"] });
    },
    onError: (e: unknown) => setAviso(e instanceof Error ? e.message : String(e)),
  });

  const aVencer = (certificados.data ?? []).filter(
    (c) => c.ativo && c.diasAteExpirar !== null && c.diasAteExpirar <= 60);
  const total = (pendentes.data?.admissoes ?? 0) + (pendentes.data?.afastamentos ?? 0)
    + (pendentes.data?.estabelecimentos ?? 0);

  return (
    <Pagina secao="Obrigações" titulo="eSocial"
            descricao="Ordem importa: o S-1005 do estabelecimento antes do S-2200 do vínculo, e a rubrica vigente na competência antes do S-1200."
            acoes={
              <button className="botao" data-variante="primario" disabled={gerar.isPending}
                      onClick={() => { setAviso(null); gerar.mutate(); }}>
                <Play aria-hidden size={15} />
                {gerar.isPending ? "Montando" : "Montar eventos"}
              </button>
            }>
      {aVencer.length > 0 && (
        <Cartao titulo="Certificado a expirar"
                subtitulo="Certificado vencido no dia 7 trava a transmissão da folha inteira do cliente">
          <ul className="eop-op">
            {aVencer.map((c) => (
              <li key={c.id} data-tom={(c.diasAteExpirar ?? 0) <= 7 ? "critico" : "atencao"}>
                <span aria-hidden className="eop-op-icone"><ShieldCheck size={15} /></span>
                <span className="min-w-0 flex-1">
                  <span className="eop-op-titulo">{c.emitidoPara ?? c.titularCnpj ?? c.modo}</span>
                  <span className="eop-op-sub">
                    {c.modo === "procuracao" ? "procuração eletrônica" : "certificado A1"}
                    {c.expiraEm ? ` · expira em ${fData(c.expiraEm)}` : ""}
                  </span>
                </span>
                <span className="num eop-op-dias" data-tom="critico">{c.diasAteExpirar}d</span>
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {aviso && (
        <p className="eop-erro" role="status">
          <TriangleAlert aria-hidden size={15} /><span>{aviso}</span>
        </p>
      )}

      {pendentes.isSuccess && total > 0 && (
        <Cartao titulo="Fatos sem evento montado"
                subtitulo="O sistema olha o EVENTO, não uma flag no cadastro: flag esquecida cria admissão que nunca é transmitida">
          <dl className="eop-dados">
            <dt>Empregador e estabelecimentos</dt>
            <dd className="num">{pendentes.data.estabelecimentos}</dd>
            <dt>Admissões sem S-2200</dt>
            <dd className="num">{pendentes.data.admissoes}</dd>
            <dt>Afastamentos sem S-2230</dt>
            <dd className="num">{pendentes.data.afastamentos}</dd>
          </dl>
        </Cartao>
      )}

      {rejeicoes.isSuccess && rejeicoes.data.length > 0 && (
        <Cartao titulo="Rejeições em aberto"
                subtitulo="Traduzidas pelo dicionário, que é dado editável e não código">
          <ul className="eop-op">
            {rejeicoes.data.map((o) => (
              <li key={o.id} data-tom="critico">
                <span aria-hidden className="eop-op-icone">
                  {o.titulo ? <TriangleAlert size={15} /> : <CircleHelp size={15} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="eop-op-titulo">
                    <span className="num">{o.codigo}</span>{" "}
                    {o.titulo ?? "código ainda não dicionarizado"}
                  </span>
                  <span className="eop-op-sub">
                    {o.eventoTipo}{o.nome ? ` · ${o.nome}` : ""}
                    {o.localizacao ? ` · ${o.localizacao}` : ""}
                  </span>
                  {o.explicacao && <span className="eop-op-explica">{o.explicacao}</span>}
                  {o.comoResolver && <span className="eop-op-resolve">{o.comoResolver}</span>}
                </span>
                {o.rotaUi && (
                  <button className="botao h-7 px-2.5 text-xs" onClick={() => ir(o.rotaUi!)}>
                    Corrigir
                  </button>
                )}
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {fila.isPending && <Carregando linhas={5} />}
      {fila.isError && <Falha erro={fila.error} tentarDeNovo={() => void fila.refetch()} />}

      {fila.isSuccess && fila.data.eventos.length === 0 && (
        <EmptyState
          titulo="Nenhum evento montado"
          descricao="Os eventos nascem dos fatos: a admissão gera S-2200, o afastamento gera S-2230, e o fechamento gera S-1200 e S-1299."
        />
      )}

      {fila.isSuccess && fila.data.eventos.length > 0 && (
        <>
          {fila.data.plano.ondas.map((onda, i) => (
            <Cartao key={i} titulo={`Onda ${i + 1}`}
                    subtitulo={i === 0
                      ? "Pode ir em paralelo. A onda seguinte só começa depois do retorno desta."
                      : "Depende do retorno da onda anterior"}>
              <ul className="eop-op">
                {onda.map(({ id }) => {
                  const e = fila.data.eventos.find((x) => x.id === id);
                  if (!e) return null;
                  const dias = e.prazoLegal
                    ? Math.round((Date.parse(e.prazoLegal + "T00:00:00Z") - Date.now()) / 86400000)
                    : null;
                  return (
                    <li key={id}
                        data-tom={e.estado === "rejeitado" ? "critico"
                          : dias !== null && dias < 0 ? "critico"
                          : dias !== null && dias <= 5 ? "atencao" : undefined}>
                      <span aria-hidden className="eop-op-icone"><Landmark size={15} /></span>
                      <span className="min-w-0 flex-1">
                        <span className="eop-op-titulo">
                          {e.tipo}{e.nome ? ` · ${e.nome}` : ""}
                        </span>
                        <span className="eop-op-sub">
                          {e.estado} · <span className="num">{e.idEvento}</span>
                          {e.ambiente === 2 ? " · produção restrita" : " · PRODUÇÃO"}
                        </span>
                      </span>
                      {dias !== null && (
                        <span className="num eop-op-dias"
                              data-tom={dias < 0 ? "critico" : dias <= 5 ? "atencao" : undefined}>
                          {dias < 0 ? `${Math.abs(dias)}d de atraso` : `${dias}d`}
                        </span>
                      )}
                      <button className="botao h-7 px-2.5 text-xs" onClick={() => setVendoXml(id)}>
                        <FileCode2 aria-hidden size={13} /> XML
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Cartao>
          ))}

          {fila.data.plano.bloqueados.length > 0 && (
            <Cartao titulo="Bloqueados"
                    subtitulo="Enviar fora de ordem é rejeição garantida: o vínculo não entra e a competência não fecha">
              <ul className="eop-op">
                {fila.data.plano.bloqueados.map((b) => {
                  const e = fila.data.eventos.find((x) => x.id === b.id);
                  return (
                    <li key={b.id} data-tom="atencao">
                      <span aria-hidden className="eop-op-icone"><TriangleAlert size={15} /></span>
                      <span className="min-w-0 flex-1">
                        <span className="eop-op-titulo">
                          {e?.tipo}{e?.nome ? ` · ${e.nome}` : ""}
                        </span>
                        <span className="eop-op-explica">{b.motivo}</span>
                        {b.faltam.length > 0 && (
                          <span className="eop-op-resolve">
                            precisa antes: {b.faltam.join(", ")}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Cartao>
          )}
        </>
      )}

      {vendoXml && <VerXml id={vendoXml} aoFechar={() => setVendoXml(null)} />}
    </Pagina>
  );
}

/**
 * O XML do evento.
 *
 * Remontado a partir do snapshot com o leiaute DAQUELA época, e não lido de um
 * campo de texto: o que é imutável é o snapshot mais a versão do leiaute, e
 * guardar o XML dobraria o armazenamento sem acrescentar garantia.
 */
function VerXml({ id, aoFechar }: { id: string; aoFechar: () => void }) {
  const q = useQuery({
    queryKey: ["esocial", "xml", id],
    queryFn: () => trpc.transmissao.xml.query({ id }),
  });

  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true" aria-label="XML do evento"
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal" style={{ maxWidth: "52rem" }}>
        <header className="painel-topo flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[0.9375rem] font-semibold">
              {q.data ? `${q.data.evento.tipo} · ${q.data.evento.nome ?? "empresa"}` : "XML"}
            </h2>
            {q.data && (
              <p className="mt-1 text-xs num" style={{ color: "var(--faint-foreground)" }}>
                {q.data.evento.idEvento} · leiaute {q.data.evento.leiauteVersao}
              </p>
            )}
          </div>
          <button className="botao-icone" aria-label="Fechar" onClick={aoFechar}>
            <X aria-hidden size={16} />
          </button>
        </header>
        <div className="painel-corpo">
          {q.isPending && <Carregando linhas={8} nu />}
          {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}
          {q.data && (
            <>
              <pre className="eop-xml">{q.data.xml}</pre>
              <p className="carimbo mt-3">
                falta a assinatura ICP-Brasil, que roda no serviço isolado com o
                certificado do cliente
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- desempenho */

export function Desempenho() {
  const q = useQuery({
    queryKey: ["operacao", "avaliacoes"],
    queryFn: () => trpc.operacao.avaliacoes.query(),
  });
  return (
    <Pagina secao="Desenvolvimento" titulo="Desempenho"
            descricao="Absenteísmo e afastamento não podem ser insumo de nota: é proibição estrutural, não recomendação.">
      <Lista q={q} icone={Target}
             vazio={{ titulo: "Nenhuma avaliação",
                      descricao: "O ciclo define quem avalia quem. Numa 360 a mesma pessoa aparece dos dois lados." }}
             mapear={(a) => ({
               chave: a.id,
               titulo: `${a.avaliado} · ${a.relacao}`,
               detalhe: (a.avaliador ? `avaliada por ${a.avaliador}` : "sem avaliador")
                 + (a.ciclo ? ` · ciclo ${a.ciclo}` : "")
                 + (a.enviadaEm ? ` · enviada em ${fData(a.enviadaEm)}` : ""),
               marca: a.enviadaEm ? "enviada" : "pendente",
               tom: a.enviadaEm ? "normal" : "atencao",
             })} />
    </Pagina>
  );
}
